import { Metric } from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import { KpiMeasure } from 'src/app/dashboards/shared/dashboard-config/kpis/kpi-columns';
import { MetricProfile } from 'src/app/dashboards/shared/dashboard-config/metrics/metric-profile';
import {
  companiesCountProfile,
  companiesRevenueProfile,
  economicParticipantsProfile,
  employedProfile,
  gvaProfile,
  selfEmployedProfile,
  tourismProfile,
} from 'src/app/dashboards/shared/dashboard-config/metrics/metric-profile-implementations';
import { GranularityProfile } from 'src/app/dashboards/shared/dashboard-config/granularities/granularity-profile';

export interface TimeSeriesBarMetricKeys {
  valueKey: string;
  valueChangeKey: string;
  valueCagr: string;
}

/** Shared chart value column names for bar transformers. */
export const TIME_SERIES_BAR_KEYS: TimeSeriesBarMetricKeys = {
  valueKey: 'value',
  valueChangeKey: 'value_change_pct',
  valueCagr: 'value_cagr',
};

/** KPI measure copied into each chart key. */
export const TIME_SERIES_BAR_MEASURES: readonly { measure: KpiMeasure; resultColumn: string }[] = [
  { measure: 'value', resultColumn: TIME_SERIES_BAR_KEYS.valueKey },
  { measure: 'changePrevYearPct', resultColumn: TIME_SERIES_BAR_KEYS.valueChangeKey },
  { measure: 'cagr', resultColumn: TIME_SERIES_BAR_KEYS.valueCagr },
];

/**
 * Time-series exceptions of a metric. Everything else comes from `base` (KPIs, temporal kind,
 * labels), the granularity profile and `ProductRules`; unset fields take the defaults below.
 */
export interface TimeSeriesBarMetricProfile {
  metricId: Metric;
  base: MetricProfile;
  /** Sub-market filter backed by the dataset rather than the product (Employed). */
  useRemoteSubMarket?: boolean;
  /** Default true; Tourism uses the layout without the map sidebar. */
  showMapSidebar?: boolean;
  /** Post-filter sort column. Default `year`. */
  sortTimeColumn?: 'year' | 'quarter';
  /** Percentage measures rewritten for export. Default: change vs previous year and CAGR. */
  exportPercentageMeasures?: KpiMeasure[];
  /** Height of the main bar labels. Default 85. */
  resolveMainBarLabelHeight?: (granularity: GranularityProfile) => number;
  /** Overrides `ENTWICKLUNG ${KPI genitive phrase}`. */
  chartHeadingLead?: (filterValues: Map<string, any>) => string;
  /** Overrides the selected KPI's nominative label. */
  mapHeadingLead?: (filterValues: Map<string, any>) => string;
}

export function timeSeriesChartHeadingLead(metric: TimeSeriesBarMetricProfile): (filterValues: Map<string, any>) => string {
  return metric.chartHeadingLead ?? ((fv) => `ENTWICKLUNG ${metric.base.formatKpiGenitivePhrase(fv)}`);
}

export function timeSeriesMapHeadingLead(metric: TimeSeriesBarMetricProfile): (filterValues: Map<string, any>) => string {
  return metric.mapHeadingLead ?? ((fv) => metric.base.formatKpiLabel(fv));
}

export const timeSeriesBarCompaniesCountProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.CompaniesCount,
  base: companiesCountProfile,
};

export const timeSeriesBarCompaniesRevenueProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.CompaniesRevenue,
  base: companiesRevenueProfile,
  resolveMainBarLabelHeight: () => 115,
};

export const timeSeriesBarEconomicParticipantProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.EconomicParticipant,
  base: economicParticipantsProfile,
};

export const timeSeriesBarEmployedProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.Employed,
  base: employedProfile,
  useRemoteSubMarket: true,
  sortTimeColumn: 'quarter',
  exportPercentageMeasures: ['changePrevYearPct', 'changePrevQuarterPct', 'cagr'],
  resolveMainBarLabelHeight: (granularity) => (granularity.traits.includes('district-level') ? 65 : 85),
};

export const timeSeriesBarGrossValueAddedProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.GrossValueAdded,
  base: gvaProfile,
};

export const timeSeriesBarSelfEmployedProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.SelfEmployed,
  base: selfEmployedProfile,
  mapHeadingLead: () => 'SOLO-SELBSTSTÄNDIGEN',
};

export const timeSeriesBarTourismProfile: TimeSeriesBarMetricProfile = {
  metricId: Metric.Tourism,
  base: tourismProfile,
  showMapSidebar: false,
  // Tourism exports no percentage columns today; decide when Tourism is reviewed.
  exportPercentageMeasures: [],
  chartHeadingLead: () => 'ENTWICKLUNG DER ANZAHL AN TOURISM',
  mapHeadingLead: () => 'TOURISM',
};
